import { useCallback, useMemo } from "react";
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApiClient, type ApiClientConfig } from "@playbookai/api-clients";
import type { DashboardRecord, DashboardType } from "@playbookai/types";
import { dashboardApi } from "./api.js";
import { dashboardKeys, type DashboardKeyScope } from "./query-keys.js";

/**
 * Ensure-on-mount dashboard configuration read (D19): the queryFn is the
 * idempotent audited `ensure` command — the backend returns the existing
 * active dashboard or instantiates the current template. Configuration fetch
 * stays separate from data resolution (§16.2).
 */

export interface UseDashboardArgs {
  readonly dashboardType: DashboardType;
  /** Required for vsm_home / lama_report; omit for personal_home. */
  readonly subjectId?: string;
  readonly scope: DashboardKeyScope;
  /** Gate until the session identity is known. */
  readonly enabled?: boolean;
}

export interface UseDashboardResult {
  readonly query: UseQueryResult<DashboardRecord>;
  readonly record: DashboardRecord | undefined;
  readonly queryKey: readonly unknown[];
  readonly config: ApiClientConfig;
  /** Replace the cached record with an authoritative server response. */
  readonly reconcile: (next: DashboardRecord) => void;
  /**
   * The freshest record straight from the query cache. `record` is a render
   * snapshot, so a second mutation issued in the same tick as the first would
   * still read the pre-`reconcile` versions and lose the CAS race against the
   * change the user just made. `peek()` sees the reconciled record
   * immediately, before React re-renders.
   */
  readonly peek: () => DashboardRecord | undefined;
  /** Cancel an in-flight ensure before an optimistic cache write. */
  readonly cancel: () => Promise<void>;
  /** Drop the cache and refetch authoritative configuration (conflict path). */
  readonly invalidate: () => void;
}

export function useDashboard(args: UseDashboardArgs): UseDashboardResult {
  const config = useApiClient();
  const queryClient = useQueryClient();
  const subjectId = args.subjectId ?? null;
  const queryKey = useMemo(
    () => dashboardKeys.bySubject(args.scope, args.dashboardType, subjectId),
    [args.scope, args.dashboardType, subjectId],
  );
  const query = useQuery<DashboardRecord>({
    queryKey,
    enabled: args.enabled !== false,
    staleTime: 30_000,
    retry: false,
    // Override the shell's conservative global defaults: this query is an
    // idempotent ensure/read, so focus/reconnect are safe recovery points for
    // a failed load and refresh stale configuration from another editor.
    //
    // This DOES point a refetch at a state-changing endpoint, and that is
    // deliberate rather than an oversight: `DashboardService.ensure` resolves an
    // EXISTING dashboard on a fast path that runs outside any transaction and
    // records no audit row (`dashboard.service.ts`). Only the first call for a
    // subject — the one that actually instantiates the template — writes, so
    // per-tab focus costs one indexed read, not an audit row and not write load.
    // If that fast path ever moves inside `withAuditedTransaction`, turn these
    // off and drive re-reads from `invalidate()` instead.
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    queryFn: () =>
      dashboardApi.ensure(config, {
        dashboardType: args.dashboardType,
        ...(subjectId !== null ? { subjectId } : {}),
      }),
  });
  const reconcile = useCallback(
    (next: DashboardRecord) => queryClient.setQueryData(queryKey, next),
    [queryClient, queryKey],
  );
  const peek = useCallback(
    () => queryClient.getQueryData<DashboardRecord>(queryKey),
    [queryClient, queryKey],
  );
  const invalidate = useCallback(
    () => void queryClient.invalidateQueries({ queryKey, exact: true }),
    [queryClient, queryKey],
  );
  const cancel = useCallback(
    () => queryClient.cancelQueries({ queryKey, exact: true }),
    [queryClient, queryKey],
  );
  return {
    query,
    record: query.data,
    queryKey,
    config,
    reconcile,
    peek,
    cancel,
    invalidate,
  };
}
